import { CloudUploadOutlined, CopyOutlined, DeleteOutlined, EllipsisOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { App, Button, Dropdown, Input, Table, Typography } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDebounce } from 'use-debounce';
import { HotkeyBadge } from '../../components/HotkeyBadge.tsx';
import { MainContent } from '../../components/MainContent';
import type { ProfilesQuery } from '../../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import type { UpdateEvent } from '../../socket/types.ts';
import { useSubscription } from '../../socket/useSubscription.ts';
import { tableActionCell } from '../../styles/globalCss';
import { hasFrontDeskRole } from '../../utils/user.ts';
import { DELETE_PROFILE_MUTATION, PROFILES_QUERY } from './Profiles.gql';
import { CopyProfileModal } from './profiles/CopyProfileModal';
import { ImportProfileModal } from './profiles/ImportProfileModal';
import { ProfileEditorDrawer } from './profiles/ProfileEditorDrawer';

export type Profile = NonNullable<ProfilesQuery['profiles']>[number];
export type ProfileParameter = Profile['parameters'][number];
export type Doctor = NonNullable<ProfilesQuery['doctorsForCurrentUser']>[number];
export type CatalogParameter = NonNullable<ProfilesQuery['catalogParameters']>[number];
export type Diagnose = NonNullable<ProfilesQuery['diagnoses']>[number];

export const Profiles: React.FC = () => {
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editProfile, setEditProfile] = useState<Profile | null>(null);
  const [copyProfile, setCopyProfile] = useState<Profile | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const { modal, message } = App.useApp();
  const auth = useAuth();
  const isFrontDesk = hasFrontDeskRole(auth.user);

  const { data, loading, refetch } = useQuery(PROFILES_QUERY, {
    variables: {
      doctorId: isFrontDesk ? primaryDoctorId : currentDoctorId,
    },
  });

  const callback = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received UI update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );

  const tenantId = isFrontDesk ? primaryDoctorId : currentDoctorId;
  useSubscription({ type: 'profile-created', tenantId: tenantId }, callback);
  useSubscription({ type: 'profile-updated', tenantId: tenantId }, callback);
  useSubscription({ type: 'profile-deleted', tenantId: tenantId }, callback);

  const [deleteProfileMutation] = useMutation(DELETE_PROFILE_MUTATION);
  const deleteProfile = async (profile: Profile) => {
    modal.confirm({
      maskClosable: true,
      title: 'Profil Löschen',
      content: (
        <>
          Wollen Sie das Profil <Typography.Text strong>{profile.name}</Typography.Text> wirklich löschen?
        </>
      ),
      okText: 'Löschen',
      cancelText: 'Abbrechen',
      okButtonProps: { icon: <DeleteOutlined /> },
      onOk: async () => {
        try {
          await deleteProfileMutation({
            variables: {
              id: profile.id,
            },
          });

          message.success('Profil wurde gelöscht');
        } catch (_e) {
          message.error('Profil konnte nicht gelöscht werden');
        }
      },
    });
  };

  const columns: ColumnsType<Profile> = [
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: value => (
        <Typography.Text style={{ fontWeight: 500 }}>
          <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
        </Typography.Text>
      ),
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Tastaturkürzel',
      dataIndex: 'hotKey',
      key: 'hotKey',
      ellipsis: true,
      width: 120,
      render: value => (value ? <HotkeyBadge hotkey={value} /> : ''),
    },
    {
      title: 'Anzahl Parameter',
      key: 'paramCount',
      ellipsis: true,
      width: 150,
      render: (_, record) => record.parameters.length,
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      ellipsis: true,
      width: '50px',
      className: tableActionCell,
      render: (_, record) => {
        const items: ItemType[] = [
          {
            label: 'Kopieren',
            key: 'copy',
            icon: <CopyOutlined />,
            onClick: () => setCopyProfile(record),
          },
          {
            label: 'Löschen',
            danger: true,
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: () => deleteProfile(record),
          },
        ];

        return (
          <Dropdown menu={{ items, onClick: e => e.domEvent.stopPropagation() }} trigger={['click']} placement="bottomRight">
            <Button
              icon={<EllipsisOutlined style={{ fontSize: '20px', verticalAlign: 'middle' }} />}
              type="text"
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const filteredProfiles: Profile[] = useMemo(
    () => filterByTokens(data?.profiles ?? [], debouncedSearch, [p => p.name]),
    [debouncedSearch, data]
  );

  const usedHotKeys = data?.profiles?.filter(it => it.hotKey)?.map(it => it.hotKey!) ?? [];

  return (
    <MainContent size="medium">
      <PageHeader
        title="Profile"
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={[
          <Input
            key="search"
            allowClear
            autoFocus
            placeholder="Suche nach Bezeichnung"
            value={search ?? ''}
            onChange={e => setSearch(e.target.value)}
            prefix={<SearchOutlined />}
            style={{ width: '250px' }}
          />,
          <Button key="import" icon={<CloudUploadOutlined />} onClick={() => setImportModalOpen(true)}>
            Profile importieren
          </Button>,
          <Button
            key="create"
            icon={<PlusCircleOutlined />}
            type="primary"
            onClick={() => {
              setEditProfile({
                id: '',
                name: '',
                parameters: [],
                hotKey: null,
                diagnose: '',
                diagnoses: [],
              });
            }}
          >
            Neues Profil anlegen
          </Button>,
        ]}
      />
      <Table<Profile>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        sticky={true}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredProfiles}
        onRow={record => ({ onClick: () => setEditProfile(record), style: { cursor: 'pointer' } })}
        pagination={false}
        loading={loading}
        columns={columns}
      />
      <CopyProfileModal
        key={copyProfile?.id}
        doctors={data?.doctorsForCurrentUser ?? []}
        currentDoctorId={isFrontDesk ? primaryDoctorId : currentDoctorId}
        profile={copyProfile}
        onClose={() => setCopyProfile(null)}
        onSuccess={() => {
          setCopyProfile(null);
        }}
      />
      <ProfileEditorDrawer
        doctorId={isFrontDesk ? primaryDoctorId : currentDoctorId}
        usedHotKeys={usedHotKeys}
        catalogParameters={data?.catalogParameters ?? []}
        diagnoses={data?.diagnoses ?? []}
        profile={editProfile}
        flipParamNames={data?.doctor?.flipParamNames ?? false}
        onClose={() => setEditProfile(null)}
        onSuccess={() => {
          setEditProfile(null);
        }}
      />
      <ImportProfileModal open={importModalOpen} onClose={() => setImportModalOpen(false)} />
    </MainContent>
  );
};
